/* eslint-disable prettier/prettier */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Accordion from './Accordion';
import LayoutContainer from './LayoutContainer';

import icUser from '@/assets/ic_user.svg';
import { Button } from '@/components';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import ROUTES from '@/router/routes';
import { AccordionItem, FaqSectionProps } from '@/types/faq';

const RegistrationAndLogin = ({ isPublic, state, title }: FaqSectionProps) => {
  const { isMobile } = useIsDesktop();
  const [activeAccordion, setActiveAccordion] = useState<string | null>(
    state?.accordionId || null
  );

  // Actualizar el acordeón activo cuando cambia el state
  useEffect(() => {
    if (state?.accordionId) {
      setActiveAccordion(state.accordionId);
    }
  }, [state]);

  const navigate = useNavigate();

  // Manejador para navegar a la edición de cuenta
  const handleEditAccountClick = () => {
    isPublic ? navigate(ROUTES.basePath) : navigate(ROUTES.editAccount.myData);
  };

  // Componente reutilizable para texto con enlaces a modificar datos
  const EditAccountLink = () => (
    <Button
      className="mx-auto font-bold"
      type="button"
      variant="text"
      onClick={handleEditAccountClick}
    >
      Modificar mis datos
    </Button>
  );

  // Datos para los acordeones
  const accordionItems: AccordionItem[] = [
    {
      id: 'registracion-como-ingreso',
      title: '¿Cómo me registro o ingreso en el sitio?',
      content: (
        <>
          <p>
            <span className="font-bold">SI NUNCA JUGASTE A GRAN DT </span>
            tenés que ingresar al sitio, desde tu computadora o celular y
            registrarte. Podés hacerlo con tu usuario de Facebook, Google, Apple
            o bien con tus datos personales creando una cuenta en el sitio.
            Además, deberás definir el nombre de tu equipo de fantasía.
          </p>
          <br />
          <p>
            Para finalizar el proceso de registración, recibirás un e-mail, que
            deberás confirmar haciendo click en él.
          </p>
          <br />
          <p>
            <span className="font-bold mt-4">
              SI YA JUGASTE A EDICIONES ANTERIORES DE GRAN DT{' '}
            </span>
            simplemente ingresá tu correo electrónico y contraseña, confirmá tus
            datos y armá tu equipo para empezar a jugar.
          </p>
        </>
      ),
    },
    {
      id: 'registracion-como-cambio-email',
      title: '¿Cómo cambio el mail de mi cuenta?',
      content: (
        <>
          <p>
            Si querés modificar tu correo electrónico, es necesario que te
            contactes con nuestro Centro de Ayuda, al{' '}
            <a className="text-blue-600 underline" href="tel:08003332343">
              0800-333-2343
            </a>
            . Allí te ayudaremos e indicaremos los pasos a seguir.
          </p>
          <br />
          <p>
            Recibirás un correo electrónico de confirmación a tu nuevo mail.
            Deberás hacer click en el mail y validar así el cambio de e-mail.
            Luego, deberás ingresar a Gran DT con el nuevo correo.
          </p>
        </>
      ),
    },
    {
      id: 'registracion-como-recupero-clave',
      title: '¿Cómo recupero o modifico mi contraseña?',
      content: (
        <>
          <p>
            Si estás jugando a Gran DT pero no recordás tu contraseña, debés
            acceder a{' '}
            <a className="text-blue-600 underline" href="https://grandt.com.ar">
              grandt.com.ar
            </a>
            , o bien a la APP y hacer click en la opción INGRESAR.
          </p>
          <br />
          <p>
            Una vez allí, ingresá tu mail, presioná Continuar y luego hacé click
            en la opción ¿Olvidaste tu contraseña?. Recibirás un correo
            electrónico para modificar tu contraseña.
          </p>
          <br />
          <p>
            Hacé click en el mail recibido y accederás a una página de Gran DT
            desde que la podrás ingresar una nueva contraseña.
          </p>
          <br />
          <p>
            La próxima vez que ingreses a Gran DT, deberás hacerlo con la nueva
            contraseña.
          </p>
        </>
      ),
    },
    {
      id: 'registracion-como-modifico-datos',
      title: '¿Cómo modifico mis datos personales?',
      content: (
        <>
          <p>
            Los datos personales se pueden editar desde la web y la APP de Gran
            DT. Iniciá sesión en el juego, hacé click en tu nombre de usuario,
            que se encuentra en el margen superior derecho, y seleccioná la
            opción <EditAccountLink />. Todos los datos pueden ser editados, a
            excepción del DNI registrado en el juego.
          </p>
          <br />
          <p>
            También podés modificar tu foto de perfil. Ingresá a{' '}
            <EditAccountLink /> y luego a{' '}
            <span className="font-bold">Cambiar Foto de Perfil</span>. Deberás{' '}
            <strong>Seleccionar</strong> la foto de perfil deseada que tengas
            guardada en el equipo y luego hacer click en{' '}
            <span className="font-bold">Confirmar</span> para que se guarde el
            cambio.
          </p>
        </>
      ),
    },
    ...(state?.accordionId === 'registracion-borrar-datos-personales'
      ? [
          {
            id: 'registracion-borrar-datos-personales',
            title: '¿Cómo elimino mi cuenta de Gran DT?',
            content: (
              <>
                <p>
                  Es posible dar de baja tu cuenta de Gran DT. Si querés hacerlo
                  porque tenés inconvenientes con el acceso a la misma o con la
                  edición de tus datos personales o con la recepción de mails no
                  deseados, te sugerimos antes pruebes las siguientes
                  alternativas:
                </p>
                <ul className="list-disc pl-6 mt-4 marker:text-blue-700 marker:font-bold">
                  <li>
                    Si tenés problemas con el acceso a tu cuenta, podés
                    gestionarlo en el Centro de Atención al Cliente:{' '}
                    <a
                      className="text-blue-600 underline"
                      href="tel:08003332343"
                      rel="noreferrer"
                      target="_blank"
                    >
                      0800-333-2343
                    </a>{' '}
                  </li>
                  <li>
                    Si lo que buscás es editar tu información, podés hacerlo
                    desde “Modificar datos personales”.
                  </li>
                  <li>
                    Si en cambio no querés recibir más correos electrónicos,
                    podés desuscribirte desde el último mail que te hayamos
                    enviado.
                  </li>
                </ul>
                <br />
                <br />
                <p>
                  Si querés avanzar con la eliminación de tu perfil, tené en
                  cuenta que <strong>no se puede revertir</strong> y que implica
                  lo siguiente:
                </p>
                <br />
                <ul className="list-disc pl-6 mt-4 marker:text-blue-700 marker:font-bold">
                  <li>
                    Se anulará tu puntaje y tu posición en todos los rankings.
                  </li>
                  <li>
                    Desaparecerá tu participación en Torneo de Amigos, Miniligas
                    y cualquier otra competencia de Gran DT.
                  </li>
                  <li>
                    Ya no podrás ingresar a Gran DT. Si lo deseás podrás crear
                    una nueva cuenta, que no tendrá relación con tu cuenta
                    actual. Es decir, no mantendrás tu historial de competencia.
                  </li>
                  <li>
                    Gran DT no mantendrá ningún dato personal que pueda
                    identificarte.
                  </li>
                </ul>
                <br />
                <br />
                <p>
                  Para iniciar el proceso de eliminación de cuenta, contactate
                  con el Centro de Atención del Cliente al{' '}
                  <a
                    className="text-blue-600 underline"
                    href="tel:08003332343"
                    rel="noreferrer"
                    target="_blank"
                  >
                    0800-333-2343
                  </a>{' '}
                  o bien envianos un mail a{' '}
                  <a
                    className="text-blue-600 underline"
                    href="mailto:consultas@grandt.com.ar"
                  >
                    consultas@grandt.com.ar
                  </a>
                  .
                </p>
                <br />
              </>
            ),
          },
        ]
      : []),
  ];

  // Manejador para alternar acordeones
  const toggleAccordion = (id: string) => {
    setActiveAccordion(activeAccordion === id ? null : id);
  };

  return (
    <LayoutContainer>
      <div className="flex gap-x-3 items-center content-center mb-8">
        {isMobile && (
          <img
            alt="Gran DT - logo calendario"
            className="w-6 h-6"
            src={icUser}
          />
        )}
        <span className={`text-lg text-[#333333] font-bold`}>{title}</span>
      </div>

      {accordionItems.map((item) => (
        <Accordion
          key={item.id}
          isOpen={activeAccordion === item.id}
          title={item.title}
          onToggle={() => toggleAccordion(item.id)}
        >
          <div className="text-sm text-gray-600">{item.content}</div>
        </Accordion>
      ))}
    </LayoutContainer>
  );
};

export default RegistrationAndLogin;
